
import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Check, ArrowRight, ArrowLeft, Leaf, TreePine, Snowflake, Sprout, Wind, Scissors, MapPin, CalendarDays, ClipboardList, User, Sparkles } from 'lucide-react';
import useScrollToTop from '@/hooks/use-scroll-to-top';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';

type FormStep = 'contact' | 'project' | 'details' | 'confirmation';

const serviceOptions = [
  { id: 'lawn-maintenance', label: 'Tonte de pelouse', icon: Leaf },
  { id: 'hedge-trimming', label: 'Taille de haies', icon: Scissors },
  { id: 'garden-planting', label: 'Terrassement / paysagement', icon: Sprout },
  { id: 'seasonal-cleanup', label: 'Nettoyage d\'automne', icon: TreePine },
  { id: 'aeration', label: 'Nettoyage de printemps', icon: Wind },
  { id: 'snow-removal', label: 'Déneigement de toiture', icon: Snowflake },
];

const propertySizes = [
  { id: 'small', label: 'Petit terrain (moins de 5 000 pi²)' },
  { id: 'medium', label: 'Terrain moyen (5 000 - 15 000 pi²)' },
  { id: 'large', label: 'Grand terrain (15 000 - 30 000 pi²)' },
  { id: 'xlarge', label: 'Très grand terrain (plus de 30 000 pi²)' },
  { id: 'commercial', label: 'Propriété commerciale' },
];

const frequencyOptions = [
  { id: 'one-time', label: 'Service unique' },
  { id: 'weekly', label: 'Hebdomadaire' },
  { id: 'biweekly', label: 'Aux 2 semaines' },
  { id: 'monthly', label: 'Mensuel' },
  { id: 'seasonal', label: 'Saisonnier (contrat)' },
];

const steps = [
  { id: 'contact', label: 'Coordonnées', icon: User, number: 1 },
  { id: 'project', label: 'Services', icon: ClipboardList, number: 2 },
  { id: 'details', label: 'Détails', icon: CalendarDays, number: 3 },
];

const Quote = () => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  useScrollToTop();
  const [currentStep, setCurrentStep] = useState<FormStep>('contact');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    selectedServices: [] as string[],
    propertySize: '',
    frequency: '',
    preferredStart: '',
    details: '',
    imageFile: null as File | null,
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const isContactStepComplete = () => {
    return formData.firstName && formData.lastName && formData.phone && formData.address && formData.city;
  };
  
  const isProjectStepComplete = () => {
    return formData.selectedServices.length > 0 && formData.propertySize !== '';
  };
  
  const handleNextStep = () => {
    if (currentStep === 'contact') setCurrentStep('project');
    else if (currentStep === 'project') setCurrentStep('details');
    else if (currentStep === 'details') void handleSubmit();
  };
  
  const handlePrevStep = () => {
    if (currentStep === 'project') setCurrentStep('contact');
    else if (currentStep === 'details') setCurrentStep('project');
  };
  
  const handleSubmit = async () => {
    setIsSubmitting(true);

    const selectedServiceLabels = formData.selectedServices.map((serviceId) =>
      serviceOptions.find((option) => option.id === serviceId)?.label ?? serviceId
    );

    const propertySizeLabel =
      propertySizes.find((size) => size.id === formData.propertySize)?.label ?? formData.propertySize;

    const frequencyLabel =
      frequencyOptions.find((frequency) => frequency.id === formData.frequency)?.label ?? formData.frequency;

    try {
      const { error } = await supabase.functions.invoke('submit-quote-lead', {
        body: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
          selectedServices: selectedServiceLabels,
          propertySize: propertySizeLabel,
          frequency: frequencyLabel,
          preferredStart: formData.preferredStart,
          details: formData.details,
          imageFileName: formData.imageFile?.name ?? '',
          submittedAt: new Date().toISOString(),
        },
      });

      if (error) {
        throw error;
      }

      setCurrentStep('confirmation');
      toast({
        title: 'Demande envoyée avec succès !',
        description: 'Nous vous contacterons très bientôt.',
      });
    } catch (error) {
      console.error('Quote submission failed', error);
      toast({
        title: 'Erreur lors de l’envoi',
        description: 'Veuillez réessayer dans quelques instants.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const toggleService = (serviceId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.includes(serviceId)
        ? prev.selectedServices.filter(s => s !== serviceId)
        : [...prev.selectedServices, serviceId]
    }));
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({ ...prev, imageFile: e.target.files![0] }));
    }
  };
  
  const getStepIndex = () => {
    const map: Record<string, number> = { contact: 0, project: 1, details: 2, confirmation: 3 };
    return map[currentStep] ?? 0;
  };

  const handleStartOver = () => {
    setFormData({
      firstName: '', lastName: '', email: '', phone: '',
      address: '', city: '', postalCode: '',
      selectedServices: [], propertySize: '', frequency: '',
      preferredStart: '', details: '', imageFile: null,
    });
    setCurrentStep('contact');
  };

  return (
    <Layout>
      {/* Hero Section - matching home page branding */}
      <div className="relative pt-20 pb-16 md:pb-24 bg-brand-green text-white overflow-hidden">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: "url('/__l5e/assets-v1/afcdce7e-af6a-467e-b24c-d58310e345c9/camion-remorque-mcbain.jpg')" }}
          ></div>
        </div>
        <div className="container-wide relative z-10 text-center pt-16 pb-8 animate-fade-in">
          <h1 className="section-title mb-4 text-balance">Demande de Soumission</h1>
          <p className="text-lg md:text-xl text-brand-stone-light max-w-2xl mx-auto leading-relaxed">
            Remplissez le formulaire ci-dessous et recevez votre soumission gratuite en 24-48h
          </p>
        </div>
      </div>
      
      {/* Form Section */}
      <section className="py-12 md:py-16 bg-brand-stone-light min-h-screen">
        <div className="max-w-3xl mx-auto px-4">
          
          {/* Step Indicator */}
          {currentStep !== 'confirmation' && (
            <div className="mb-10">
              <div className="flex items-center justify-between relative">
                {/* Progress line */}
                <div className="absolute top-6 left-0 right-0 h-0.5 bg-brand-stone-dark z-0 mx-12"></div>
                <div 
                  className="absolute top-6 left-0 h-0.5 bg-brand-green z-0 transition-all duration-500 mx-12"
                  style={{ width: `${getStepIndex() * 50}%` }}
                ></div>
                
                {steps.map((step, index) => {
                  const isActive = getStepIndex() === index;
                  const isCompleted = getStepIndex() > index;
                  const StepIcon = step.icon;
                  return (
                    <div key={step.id} className="flex flex-col items-center relative z-10">
                      <div className={cn(
                        "w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300",
                        isCompleted ? "bg-brand-green text-white shadow-lg" :
                        isActive ? "bg-brand-green text-white ring-4 ring-brand-green/20 shadow-lg" :
                        "bg-white text-brand-stone-dark border-2 border-brand-stone-dark"
                      )}>
                        {isCompleted ? <Check size={20} /> : <StepIcon size={20} />}
                      </div>
                      <span className={cn(
                        "mt-2 text-xs md:text-sm font-medium font-sans transition-colors",
                        isActive || isCompleted ? "text-brand-green-dark" : "text-brand-stone-dark"
                      )}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Form Card */}
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border border-brand-stone-dark/10">
            {/* Green top accent bar */}
            <div className="h-1.5 bg-brand-green w-full"></div>
            
            {/* Confirmation */}
            {currentStep === 'confirmation' ? (
              <div className="text-center px-8 py-16 md:px-16">
                <div className="relative inline-block mb-8">
                  <div className="w-24 h-24 bg-brand-sage/20 rounded-full flex items-center justify-center animate-fade-in">
                    <div className="w-16 h-16 bg-brand-green rounded-full flex items-center justify-center">
                      <Check className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <Sparkles className="absolute -top-2 -right-2 h-6 w-6 text-brand-sage animate-pulse" />
                </div>
                
                <h2 className="font-display text-3xl md:text-4xl font-medium text-brand-green-dark mb-4">
                  Merci, {formData.firstName} !
                </h2>
                <p className="font-sans text-lg text-foreground/70 mb-8 max-w-md mx-auto leading-relaxed">
                  Votre demande de soumission a été envoyée avec succès. Notre équipe l'examinera attentivement.
                </p>
                
                <div className="bg-brand-stone-light rounded-lg p-6 mb-10 max-w-sm mx-auto text-left space-y-4">
                  <h3 className="font-display text-lg font-medium text-brand-green-dark text-center mb-4">Prochaines étapes</h3>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-brand-green text-white flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5 font-sans">1</div>
                    <p className="text-sm text-foreground/70 font-sans">Nous analysons votre demande dans les 24-48h</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-brand-green text-white flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5 font-sans">2</div>
                    <p className="text-sm text-foreground/70 font-sans">Un membre de notre équipe vous contactera pour planifier une visite si nécessaire</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-brand-green text-white flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5 font-sans">3</div>
                    <p className="text-sm text-foreground/70 font-sans">Vous recevrez votre soumission détaillée et personnalisée</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button onClick={handleStartOver} variant="outline" size="lg">
                    Nouvelle demande
                  </Button>
                  <Button href="/" variant="primary" size="lg">
                    Retour à l'accueil
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-8 md:p-10">
                {/* Step 1: Contact */}
                {currentStep === 'contact' && (
                  <>
                    <div className="flex items-center gap-3 mb-8">
                      <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center">
                        <User size={20} className="text-brand-green" />
                      </div>
                      <div>
                        <h2 className="font-display text-2xl font-medium text-brand-green-dark">Vos coordonnées</h2>
                        <p className="text-sm text-muted-foreground font-sans">Pour que nous puissions vous rejoindre</p>
                      </div>
                    </div>
                    
                    <div className="space-y-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="firstName" className="block text-sm font-semibold text-foreground mb-1.5 font-sans">
                            Prénom <span className="text-destructive">*</span>
                          </label>
                          <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required
                            className="w-full px-4 py-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring transition-all font-sans"
                            placeholder="Jean" />
                        </div>
                        <div>
                          <label htmlFor="lastName" className="block text-sm font-semibold text-foreground mb-1.5 font-sans">
                            Nom <span className="text-destructive">*</span>
                          </label>
                          <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required
                            className="w-full px-4 py-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring transition-all font-sans"
                            placeholder="Tremblay" />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="phone" className="block text-sm font-semibold text-foreground mb-1.5 font-sans">
                            Téléphone <span className="text-destructive">*</span>
                          </label>
                          <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required
                            className="w-full px-4 py-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring transition-all font-sans"
                            placeholder="(418) 123-4567" />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-1.5 font-sans">
                            Courriel <span className="text-muted-foreground font-normal">(optionnel)</span>
                          </label>
                          <input type="email" id="email" name="email" value={formData.email} onChange={handleChange}
                            className="w-full px-4 py-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring transition-all font-sans"
                            placeholder="jean@exemple.com" />
                        </div>
                      </div>
                      
                      <div className="pt-2">
                        <div className="flex items-center gap-2 mb-3">
                          <MapPin size={16} className="text-brand-green" />
                          <span className="text-sm font-semibold text-foreground font-sans">Adresse du terrain</span>
                        </div>
                        <div className="space-y-4">
                          <input type="text" id="address" name="address" value={formData.address} onChange={handleChange} required
                            className="w-full px-4 py-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring transition-all font-sans"
                            placeholder="123 Rue Principale" />
                          <div className="grid grid-cols-2 gap-4">
                            <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} required
                              className="w-full px-4 py-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring transition-all font-sans"
                              placeholder="Ville *" />
                            <input type="text" id="postalCode" name="postalCode" value={formData.postalCode} onChange={handleChange}
                              className="w-full px-4 py-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring transition-all font-sans"
                              placeholder="Code postal" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Step 2: Services */}
                {currentStep === 'project' && (
                  <>
                    <div className="flex items-center gap-3 mb-8">
                      <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center">
                        <ClipboardList size={20} className="text-brand-green" />
                      </div>
                      <div>
                        <h2 className="font-display text-2xl font-medium text-brand-green-dark">Services souhaités</h2>
                        <p className="text-sm text-muted-foreground font-sans">Sélectionnez un ou plusieurs services</p>
                      </div>
                    </div>
                    
                    <div className="space-y-8">
                      {/* Multi-select services */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {serviceOptions.map((service) => {
                          const isSelected = formData.selectedServices.includes(service.id);
                          const Icon = service.icon;
                          return (
                            <button
                              key={service.id}
                              type="button"
                              onClick={() => toggleService(service.id)}
                              className={cn(
                                "flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-200 text-left",
                                isSelected
                                  ? "border-brand-green bg-brand-green/5 shadow-sm"
                                  : "border-input hover:border-brand-sage hover:bg-muted/50"
                              )}
                            >
                              <div className={cn(
                                "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors",
                                isSelected ? "bg-brand-green text-white" : "bg-brand-stone text-brand-green-dark"
                              )}>
                                <Icon size={20} />
                              </div>
                              <span className={cn(
                                "font-sans font-medium text-sm",
                                isSelected ? "text-brand-green-dark" : "text-foreground"
                              )}>
                                {service.label}
                              </span>
                              {isSelected && (
                                <Check size={18} className="ml-auto text-brand-green flex-shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                      
                      {/* Property size */}
                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-3 font-sans">
                          Taille approximative du terrain <span className="text-destructive">*</span>
                        </label>
                        <div className="space-y-2">
                          {propertySizes.map((size) => (
                            <label key={size.id} className={cn(
                              "flex items-center p-3.5 rounded-lg border-2 cursor-pointer transition-all",
                              formData.propertySize === size.id
                                ? "border-brand-green bg-brand-green/5"
                                : "border-input hover:bg-muted/50"
                            )}>
                              <input type="radio" name="propertySize" value={size.id} checked={formData.propertySize === size.id} onChange={handleChange} className="sr-only" />
                              <span className={cn(
                                "w-5 h-5 rounded-full border-2 flex-shrink-0 mr-3 flex items-center justify-center transition-colors",
                                formData.propertySize === size.id ? "border-brand-green bg-brand-green" : "border-brand-stone-dark"
                              )}>
                                {formData.propertySize === size.id && <span className="w-2 h-2 rounded-full bg-white"></span>}
                              </span>
                              <span className="text-sm text-foreground font-sans">{size.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Step 3: Details */}
                {currentStep === 'details' && (
                  <>
                    <div className="flex items-center gap-3 mb-8">
                      <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center">
                        <CalendarDays size={20} className="text-brand-green" />
                      </div>
                      <div>
                        <h2 className="font-display text-2xl font-medium text-brand-green-dark">Détails & préférences</h2>
                        <p className="text-sm text-muted-foreground font-sans">Aidez-nous à mieux comprendre vos besoins</p>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      {/* Frequency */}
                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-3 font-sans">
                          Fréquence souhaitée
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {frequencyOptions.map((opt) => (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, frequency: opt.id }))}
                              className={cn(
                                "px-4 py-2.5 rounded-full border-2 text-sm font-medium font-sans transition-all",
                                formData.frequency === opt.id
                                  ? "border-brand-green bg-brand-green text-white"
                                  : "border-brand-stone-dark text-foreground hover:border-brand-sage"
                              )}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Preferred start */}
                      <div>
                        <label htmlFor="preferredStart" className="block text-sm font-semibold text-foreground mb-1.5 font-sans">
                          Quand aimeriez-vous commencer ?
                        </label>
                        <select id="preferredStart" name="preferredStart" value={formData.preferredStart} onChange={handleChange}
                          className="w-full px-4 py-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring transition-all font-sans">
                          <option value="">Choisir...</option>
                          <option value="asap">Dès que possible</option>
                          <option value="this-month">Ce mois-ci</option>
                          <option value="next-month">Le mois prochain</option>
                          <option value="spring">Au printemps</option>
                          <option value="planning">Je planifie pour plus tard</option>
                        </select>
                      </div>
                      
                      {/* Details */}
                      <div>
                        <label htmlFor="details" className="block text-sm font-semibold text-foreground mb-1.5 font-sans">
                          Détails supplémentaires ou questions
                        </label>
                        <textarea id="details" name="details" value={formData.details} onChange={handleChange} rows={4}
                          className="w-full px-4 py-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring transition-all resize-none font-sans"
                          placeholder="État actuel du terrain, besoins spécifiques, allergies aux produits, accès au terrain..."
                        ></textarea>
                      </div>
                      
                      {/* Photo upload */}
                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-1.5 font-sans">
                          Photo de votre terrain <span className="text-muted-foreground font-normal">(optionnel)</span>
                        </label>
                        <div className={cn(
                          "border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer",
                          formData.imageFile ? "border-brand-green bg-brand-green/5" : "border-brand-stone-dark hover:border-brand-sage"
                        )}>
                          <label htmlFor="file-upload" className="cursor-pointer">
                            <svg className="mx-auto h-10 w-10 text-brand-stone-dark mb-3" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <p className="text-sm text-foreground/70 font-sans">
                              <span className="text-brand-green font-medium">Choisir un fichier</span> ou glisser-déposer
                            </p>
                            <p className="text-xs text-muted-foreground mt-1 font-sans">PNG, JPG jusqu'à 10MB</p>
                            <input id="file-upload" type="file" className="sr-only" onChange={handleFileChange} accept="image/*" />
                          </label>
                        </div>
                        {formData.imageFile && (
                          <p className="mt-2 text-sm text-brand-green font-medium flex items-center gap-1 font-sans">
                            <Check size={14} /> {formData.imageFile.name}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}
                
                {/* Navigation */}
                <div className="mt-10 flex justify-between items-center pt-6 border-t border-border">
                  {currentStep !== 'contact' ? (
                    <Button type="button" variant="ghost" onClick={handlePrevStep}>
                      <ArrowLeft size={18} className="mr-2" /> Retour
                    </Button>
                  ) : <div />}
                  
                  <Button
                    type="button"
                    variant="primary"
                    size="lg"
                    onClick={handleNextStep}
                    disabled={
                      (currentStep === 'contact' && !isContactStepComplete()) ||
                      (currentStep === 'project' && !isProjectStepComplete()) ||
                      isSubmitting
                    }
                    className="min-w-[180px]"
                  >
                    {currentStep === 'details'
                      ? (isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande')
                      : <>Continuer <ArrowRight size={18} className="ml-2" /></>
                    }
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          {/* Trust indicators */}
          {currentStep !== 'confirmation' && (
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground font-sans">
              <div className="flex items-center gap-2">
                <Check size={16} className="text-brand-green" />
                <span>Soumission gratuite</span>
              </div>
              <div className="flex items-center gap-2">
                <Check size={16} className="text-brand-green" />
                <span>Réponse en 24-48h</span>
              </div>
              <div className="flex items-center gap-2">
                <Check size={16} className="text-brand-green" />
                <span>Sans engagement</span>
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Quote;
